import type { AnyAction } from '@reduxjs/toolkit';
import { layersToWrite, playerLayersFor, resolveSetting } from './resolveSetting';
import { getSettingDefinition, type SettingKey, type SettingValue } from './settingsRegistry';
import { selectSettingLayerValue, settingLayerValueSet } from './settingsLayersSlice';
import type {
  PlayerLayer,
  ResolvedSetting,
  SettingContext,
  SettingDefinition,
  SettingLayers,
  SettingsState,
  StoreBinding,
} from './settingTypes';

/**
 * The binding for one player layer, when today's code has a store the model
 * can use there. A layer without a binding is kept in memory (settingsLayers).
 */
export const bindingFor = <T>(
  definition: SettingDefinition<T>,
  layer: PlayerLayer,
  context: SettingContext,
): StoreBinding<T> | undefined => {
  switch (layer) {
    case 'global':
      return definition.bindings?.global;
    case 'mode':
      return context.mode ? definition.bindings?.mode?.[context.mode] : undefined;
    case 'instance':
      return undefined;
    default:
      return undefined;
  }
};

const readPlayerLayer = <T>(
  state: SettingsState,
  definition: SettingDefinition<T>,
  layer: PlayerLayer,
  context: SettingContext,
): T | undefined => {
  const binding = bindingFor(definition, layer, context);
  if (binding) {
    const stored = binding.select(state);
    return stored === undefined ? undefined : definition.parse(stored);
  }
  const kept = selectSettingLayerValue(state, definition.key, layer, context);
  return kept === undefined ? undefined : definition.parse(kept);
};

/** Every layer's value for a setting, read from today's stores and the in-memory layers. */
export const readSettingLayers = <T>(
  state: SettingsState,
  definition: SettingDefinition<T>,
  context: SettingContext = {},
  authored?: T,
): SettingLayers<T> =>
  playerLayersFor(definition.scope).reduce<SettingLayers<T>>(
    (layers, layer) => ({ ...layers, [layer]: readPlayerLayer(state, definition, layer, context) }),
    { authored: authored === undefined ? undefined : definition.parse(authored) },
  );

/** A setting's value and where it came from. `authored` is the content's value, if it sets one. */
export const selectResolvedSetting = <K extends SettingKey>(
  state: SettingsState,
  key: K,
  context: SettingContext = {},
  authored?: SettingValue<K>,
): ResolvedSetting<SettingValue<K>> => {
  const definition = getSettingDefinition(key);
  return resolveSetting(definition, readSettingLayers(state, definition, context, authored));
};

/** A setting's value: the cascade's winner. */
export const selectSetting = <K extends SettingKey>(
  state: SettingsState,
  key: K,
  context: SettingContext = {},
  authored?: SettingValue<K>,
): SettingValue<K> => selectResolvedSetting(state, key, context, authored).value;

/** The narrowest layer a player's change goes to, given what the context names. */
export const defaultWriteLayer = (
  definition: Pick<SettingDefinition<unknown>, 'key' | 'scope'>,
  context: SettingContext,
): PlayerLayer => {
  if (definition.scope === 'instance' && context.mode && context.instanceId) return 'instance';
  if (definition.scope !== 'global' && context.mode) return 'mode';
  if (definition.scope === 'global') return 'global';
  throw new Error(`Setting ${definition.key} is ${definition.scope}-scoped: name the mode`);
};

/**
 * The actions that make a player's change: the existing action for each layer
 * today's code stores, the in-memory layer for the rest. A feel value changed
 * for one piece / exercise / session also becomes the mode default.
 */
export const settingWriteActions = <K extends SettingKey>(
  key: K,
  value: SettingValue<K>,
  context: SettingContext = {},
  layer?: PlayerLayer,
): AnyAction[] => {
  const definition = getSettingDefinition(key);
  const parsed = definition.parse(value);
  return layersToWrite(definition, layer ?? defaultWriteLayer(definition, context)).map(
    (target) => {
      const binding = bindingFor(definition, target, context);
      return binding
        ? binding.write(parsed)
        : settingLayerValueSet({ key: definition.key, layer: target, context, value: parsed });
    },
  );
};
